/**
 * ============================================================================
 * OBP Mesure : ce que les visiteurs voient et touchent, section par section
 * ============================================================================
 * Aucun réglage bloc par bloc : le script reconnaît tout seul les sections
 * Shopify (`.shopify-section`) et les blocs (classes `obp-…`, blocs d'apps,
 * composants du thème). Un nouveau bloc posé dans l'éditeur est donc mesuré
 * dès sa mise en ligne, sans toucher à ce fichier.
 *
 * Ce qu'il envoie au worker `obp-mesure` :
 *   pv    une page vue (v = hauteur de la page en px)
 *   sec   une section restée à l'écran (v = millisecondes visibles)
 *   ck    un appui sur un élément cliquable
 *   dead  un appui sur un élément qui ne fait rien
 *   rage  des appuis répétés au même endroit (agacement)
 *   atc   un ajout au panier réussi (événement du thème, pas le simple clic)
 *   co    un départ vers le paiement
 *   err   une erreur JavaScript
 *
 * Vie privée : aucun cookie, aucune donnée personnelle, aucun texte saisi.
 * L'identifiant de visite vit dans l'onglet (sessionStorage) et disparaît
 * avec lui. Un refus de la mesure d'audience dans le bandeau coupe l'envoi.
 * ============================================================================
 */
(function () {
  if (window.__obpMesureInit) return;
  window.__obpMesureInit = true;

  const CFG = window.__obpM || {};
  if (!CFG.u || !navigator.sendBeacon) return;

  const ATTR = '_obp_tests';
  const clean = (s, n) => (s || '').replace(/\s+/g, ' ').trim().slice(0, n);
  const rid = () => Math.random().toString(36).slice(2, 12) + Date.now().toString(36).slice(-4);

  // ── Refus de la mesure d'audience : on n'envoie rien ─────────────────────
  function refused() {
    try {
      const cp = window.Shopify && window.Shopify.customerPrivacy;
      const c = cp && typeof cp.currentVisitorConsent === 'function' && cp.currentVisitorConsent();
      return !!c && c.analytics === 'no';
    } catch (e) { return false; }
  }

  // ── La visite ────────────────────────────────────────────────────────────
  function source() {
    const q = new URLSearchParams(location.search);
    const utm = q.get('utm_source');
    if (utm) return clean(utm.toLowerCase(), 40);
    if (q.get('fbclid')) return 'facebook';
    if (q.get('gclid') || q.get('gbraid') || q.get('wbraid')) return 'google';
    if (q.get('_kx')) return 'klaviyo';
    try {
      const host = document.referrer && new URL(document.referrer).hostname.replace(/^www\./, '');
      if (host && host !== location.hostname.replace(/^www\./, '')) return host.slice(0, 40);
    } catch (e) { /* référent illisible */ }
    return 'direct';
  }

  let visit;
  try { visit = JSON.parse(sessionStorage.getItem('obp_m') || 'null'); } catch (e) { visit = null; }
  const landing = !visit;
  if (!visit) visit = { sid: rid(), src: source() };
  try { sessionStorage.setItem('obp_m', JSON.stringify(visit)); } catch (e) { /* navigation privée */ }

  const w = window.innerWidth;
  const head = {
    sid: visit.sid,
    pid: rid(),
    path: location.pathname,
    tpl: CFG.tpl || '',
    dev: w < 750 ? 'm' : w < 1100 ? 't' : 'd',
    src: visit.src,
    land: landing ? 1 : 0,
    theme: CFG.theme || 0,
    ab: '',
  };

  // Les tirages vivent dans des cookies `obp_t_<test>` : on les relit ici pour
  // que la variante suive le visiteur sur TOUTES les pages, y compris celles
  // qui ne portent pas de repère de test (la fiche après la collection).
  function tests() {
    const t = Object.assign({}, window.__obpTests || {});
    (document.cookie.match(/obp_t_[a-z0-9-]+=[AB]/g) || []).forEach((c) => {
      const kv = c.slice(6).split('=');
      if (!t[kv[0]]) t[kv[0]] = kv[1];
    });
    return Object.keys(t).sort().map((k) => k + ':' + t[k]).join(',').slice(0, 120);
  }

  // ── File d'envoi ─────────────────────────────────────────────────────────
  let queue = [];
  let stopped = false;
  function push(e) {
    if (stopped) return;
    queue.push(e);
    if (queue.length >= 20) flush();
  }
  function flush() {
    if (stopped || !queue.length) return;
    if (refused()) { stopped = true; queue = []; return; }
    head.ab = tests();
    const body = JSON.stringify({ s: head, e: queue.splice(0, 60) });
    try {
      navigator.sendBeacon(CFG.u, new Blob([body], { type: 'text/plain' }));
    } catch (e) { /* envoi impossible : sans conséquence pour le visiteur */ }
  }
  setInterval(flush, 10000);

  // ── Sections : clé, rang, titre ──────────────────────────────────────────
  const meta = new WeakMap();
  function sectionKey(el) {
    return (el.id || '').replace(/^shopify-section-/, '').replace(/^(template|sections)--\d+__/, '').slice(0, 80);
  }
  function describe(el, section, blk) {
    const h = el.querySelector('h1, h2, h3, h4, summary');
    const m = { key: sectionKey(section), pos: 0, blk: blk, txt: clean(h && h.textContent, 80), ms: 0, since: 0, on: false };
    meta.set(el, m);
    return m;
  }

  // Blocs suivis à l'intérieur d'une section : blocs d'apps, blocs OBP, et les
  // composants du thème qui comptent dans la décision d'achat.
  const BLOCKS = '[id^="shopify-block-"], [class^="obp-"], media-gallery, product-price, obp-offers-component, '
    + 'product-form-component, gift-message-component, comparison-slider-component, accordion-custom';
  function blockName(el) {
    if (el.id && el.id.indexOf('shopify-block-') === 0) {
      return el.id.replace(/^shopify-block-[^_]*__/, '').replace(/_[A-Za-z0-9]{6}$/, '').slice(0, 60);
    }
    const cls = typeof el.className === 'string' && el.className.match(/^(obp-[a-z0-9]+)(?:\s|$)/);
    if (cls) return cls[1];
    return el.tagName.indexOf('-') > 0 ? el.tagName.toLowerCase().slice(0, 60) : null;
  }

  const sections = [];
  const zones = [];
  let io = null;
  function scan() {
    document.querySelectorAll('.shopify-section').forEach((el) => {
      if (meta.has(el) || !el.id || el.offsetHeight < 8) return;
      sections.push(el);
      // L'en-tête reste collé en haut de l'écran : le chronométrer n'apprend rien.
      if (el.classList.contains('shopify-section-group-header-group')) { describe(el, el, null); return; }
      zones.push(el);
      describe(el, el, null);
      if (io) io.observe(el);
    });
    sections.forEach((section) => {
      if (section.classList.contains('shopify-section-group-header-group')) return;
      section.querySelectorAll(BLOCKS).forEach((el) => {
        if (meta.has(el) || el.offsetHeight < 8) return;
        const name = blockName(el);
        if (!name) return;
        // Seul le bloc le plus englobant est suivi, pas ses sous-parties.
        for (let up = el.parentElement; up && up !== section; up = up.parentElement) {
          if (meta.has(up)) return;
        }
        zones.push(el);
        describe(el, section, name);
        if (io) io.observe(el);
      });
    });
    // Rang = ordre d'apparition dans la page, de haut en bas.
    sections
      .filter((el) => el.isConnected)
      .sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top)
      .forEach((el, i) => { meta.get(el).pos = i + 1; });
    zones.forEach((el) => {
      const m = meta.get(el);
      const section = m.blk && el.closest('.shopify-section');
      if (section && meta.get(section)) m.pos = meta.get(section).pos;
    });
  }

  // Une section compte comme « à l'écran » si la moitié d'elle est visible,
  // ou si elle occupe la moitié de l'écran (cas des sections très hautes).
  function setVisible(el, on) {
    const m = meta.get(el);
    if (!m || m.on === on) return;
    const now = performance.now();
    if (m.on) m.ms += now - m.since;
    m.on = on;
    m.since = now;
  }
  if ('IntersectionObserver' in window) {
    io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        const vh = window.innerHeight || 1;
        const on = en.isIntersecting
          && (en.intersectionRatio >= 0.5 || en.intersectionRect.height >= vh * 0.5);
        setVisible(en.target, on && !idle && document.visibilityState === 'visible');
      });
    }, { threshold: [0, 0.1, 0.25, 0.5, 0.75, 1] });
  }

  // Un onglet ouvert sans personne devant ne doit ni gonfler les temps d'écran
  // ni remplir la base : sans geste depuis 30 s, on arrête de compter.
  let lastTouch = performance.now();
  let idle = false;
  function visibleNow(el) {
    const vh = window.innerHeight || 1;
    const r = el.getBoundingClientRect();
    const seen = Math.max(0, Math.min(r.bottom, vh) - Math.max(r.top, 0));
    return r.height > 0 && (seen >= r.height * 0.5 || seen >= vh * 0.5);
  }
  function touched() {
    lastTouch = performance.now();
    if (!idle) return;
    idle = false;
    zones.forEach((el) => setVisible(el, visibleNow(el)));
  }
  ['scroll', 'touchstart', 'pointermove', 'keydown'].forEach((name) => {
    window.addEventListener(name, touched, { passive: true, capture: true });
  });
  setInterval(() => {
    if (idle || performance.now() - lastTouch < 30000) return;
    collectSections(true);
    idle = true;
    flush();
  }, 5000);

  function collectSections(pause) {
    const now = performance.now();
    zones.forEach((el) => {
      const m = meta.get(el);
      if (m.on) { m.ms += now - m.since; m.since = now; if (pause) m.on = false; }
      if (m.ms >= 250) {
        queue.push({ t: 'sec', sec: m.key, pos: m.pos, blk: m.blk, txt: m.txt, v: Math.round(m.ms) });
        m.ms = 0;
      }
    });
  }

  // ── Appuis ───────────────────────────────────────────────────────────────
  const INTERACTIVE = 'a, button, input, select, textarea, label, summary, [role="button"], [role="tab"], [onclick]';
  const OVERLAYS = [
    // Motifs volontairement étroits : un motif large (« consent », « cookie »)
    // accroche une classe posée sur <body> et étiquette tout à tort.
    ['cookies', 'csm-cookie-consent'],
    ['klaviyo', '[class*="klaviyo" i], [class*="kl-private"], [class*="kl-teaser"]'],
    ['avis', '[class*="inflate" i]'],
  ];

  function blockOf(target, section) {
    // D'abord le bloc suivi qui contient l'appui, pour que appuis et temps
    // d'écran portent le même nom ; à défaut, le composant le plus proche.
    const drawer = target.closest('cart-drawer-component');
    if (drawer) return 'cart-drawer-component';
    let near = null;
    for (let el = target; el && el !== section && el !== document.body; el = el.parentElement) {
      const m = meta.get(el);
      if (m && m.blk) return m.blk;
      if (!near) {
        const cls = typeof el.className === 'string' && el.className.match(/(?:^|\s)(obp-[a-z0-9]+)/);
        near = cls ? cls[1] : el.tagName.indexOf('-') > 0 ? el.tagName.toLowerCase().slice(0, 60) : null;
      }
    }
    return near;
  }

  function label(el) {
    if (!el) return '';
    if (el.tagName === 'INPUT' || el.tagName === 'SELECT' || el.tagName === 'TEXTAREA') {
      // Jamais la valeur saisie : seulement le nom du champ.
      return clean(el.getAttribute('aria-label') || el.name || el.type, 60);
    }
    return clean(el.getAttribute('aria-label') || el.textContent || el.getAttribute('alt') || el.getAttribute('title'), 60);
  }

  let lastClick = null;
  let taps = [];
  let lastRage = 0;
  let lastKey = '';
  let lastAt = 0;

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target || !event.isTrusted) return;
    // Dans un composant fermé sur lui-même (bandeau cookies), event.target
    // désigne l'enveloppe : le vrai élément touché est en tête du chemin.
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    const inner = path[0] instanceof Element ? path[0] : target;
    touched();

    const section = target.closest('.shopify-section');
    let blk = blockOf(target, section);
    if (!section) {
      const hit = OVERLAYS.find((o) => target.closest(o[1]));
      if (hit) blk = hit[0];
    }
    const m = section && meta.get(section);
    const hot = inner.closest(INTERACTIVE) || target.closest(INTERACTIVE);
    const e = {
      t: hot ? 'ck' : 'dead',
      sec: m ? m.key : section ? sectionKey(section) : null,
      pos: m ? m.pos : null,
      blk: blk,
      el: (hot || inner).tagName.toLowerCase(),
      txt: label(hot || (inner.tagName === 'IMG' ? inner : null)),
    };

    // Un <label> relaie le clic vers son champ : un geste, un seul fait.
    const key = e.t + '|' + e.sec + '|' + e.blk;
    const now = Date.now();
    if (key === lastKey && now - lastAt < 300) return;
    lastKey = key;
    lastAt = now;
    push(e);
    if (hot && e.el === 'button') lastClick = { at: now, sec: e.sec, pos: e.pos, blk: e.blk, txt: e.txt };

    if (hot && hot.closest('[name="checkout"], [href*="/checkout"], form[action*="/checkout"]')) {
      push({ t: 'co', sec: e.sec, pos: e.pos, blk: e.blk, el: e.el, txt: e.txt });
      flush();
    }

    // Agacement : trois appuis en moins d'une seconde dans un rayon de 30 px.
    taps = taps.filter((p) => now - p.at < 1000);
    taps.push({ at: now, x: event.clientX, y: event.clientY });
    const near = taps.filter((p) => Math.abs(p.x - event.clientX) < 30 && Math.abs(p.y - event.clientY) < 30);
    if (near.length >= 3 && now - lastRage > 3000) {
      lastRage = now;
      push({ t: 'rage', sec: e.sec, pos: e.pos, blk: e.blk, el: e.el, txt: e.txt });
    }
  }, true);

  // ── Ajout au panier : l'événement du thème, donc un ajout RÉUSSI ─────────
  let items = null;

  document.addEventListener('cart:update', (event) => {
    const d = event.detail || {};
    const data = d.data || {};
    if (data.didError) return;
    const count = Number((d.resource && d.resource.item_count) != null ? d.resource.item_count : data.itemCount);
    const grew = Number.isFinite(count) && (items === null ? count > 0 && !!lastClick : count > items);
    if (Number.isFinite(count)) items = count;
    if (!grew) return;
    const from = lastClick && Date.now() - lastClick.at < 8000 ? lastClick : {};
    push({ t: 'atc', sec: from.sec || null, pos: from.pos || null, blk: from.blk || null, el: 'button', txt: from.txt || '', v: count });
    flush();
    setTimeout(writeTests, 1200);
  });

  // ── Les tests en cours suivent la commande (attribut de panier masqué) ───
  let written = '';
  async function writeTests() {
    const value = tests();
    if (!value || value === written) return;
    try {
      const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
      if (!cart || !cart.item_count) return;
      if (cart.attributes && cart.attributes[ATTR] === value) { written = value; return; }
      const body = { attributes: {} };
      body.attributes[ATTR] = value;
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      written = value;
    } catch (e) { /* panier injoignable : on réessaiera au prochain ajout */ }
  }

  // ── Erreurs JavaScript (cinq par page au plus) ───────────────────────────
  let errors = 0;
  window.addEventListener('error', (event) => {
    if (errors >= 5 || !event.message) return;
    errors += 1;
    const file = (event.filename || '').split('/').pop().split('?')[0];
    push({ t: 'err', el: clean(file, 40), txt: clean(event.message, 80), v: event.lineno || 0 });
  });

  // ── Démarrage et fin de page ─────────────────────────────────────────────
  function start() {
    scan();
    push({ t: 'pv', v: Math.round(document.documentElement.scrollHeight) });
    flush();
    // Les sections chargées plus tard (avis, recommandations) sont reprises.
    setTimeout(scan, 2500);
    setTimeout(scan, 8000);
    setTimeout(writeTests, 3000);
  }

  function leave() {
    collectSections(true);
    flush();
  }
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') { leave(); return; }
    // Retour sur l'onglet : on relance le chronomètre des sections à l'écran.
    lastTouch = performance.now();
    idle = false;
    zones.forEach((el) => setVisible(el, visibleNow(el)));
  });
  window.addEventListener('pagehide', leave);
  setInterval(() => { if (document.visibilityState === 'visible') { collectSections(false); } }, 60000);

  // Contrôle depuis la console : __obpMesureEtat() liste ce qui est suivi.
  window.__obpMesureEtat = () => zones.map((el) => {
    const m = meta.get(el);
    return { section: m.key, rang: m.pos, bloc: m.blk, titre: m.txt, visible: m.on, ms: Math.round(m.ms + (m.on ? performance.now() - m.since : 0)) };
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
